import { Routes } from '@angular/router';
import { ConstruyoLandingComponent } from '@landing/components/construyo-landing/construyo-landing';
import { LoginComponent } from './features/auth/pages/login.component';
import { RegisterComponent } from './features/auth/pages/register/register.component';
import { ProfessionalProfilePageComponent } from './features/profile/pages/professional-profile/professional-profile.page';
import { maestroGuard } from '@services/guards/maestro.guard';
import { domainRedirectGuard } from '@services/guards/domain-redirect.guard';
import { QuoteFlowGuard } from '@guards/quote-flow.guard';
import { CalculatorFlowGuard } from '@guards/calculator-flow.guard';
import { ResetPasswordComponent } from './features/auth/pages/reset-password/reset-password.component';
import { APP_ROUTES, APP_ROUTES_WITH_PARAMS, NAVIGATION_ROUTES } from '@shared/constants';

export const routes: Routes = [
  // Landing pages separadas
  {
    path: APP_ROUTES.ROOT,
    component: ConstruyoLandingComponent,
    canActivate: [domainRedirectGuard] // Redirige a /owner si el dominio es construyo.pe
  }, 

  // Rutas públicas compartidas
  { path: APP_ROUTES.LOGIN, component: LoginComponent },
  { path: APP_ROUTES.REGISTER, component: RegisterComponent },
  { path: APP_ROUTES.RESET_PASSWORD, component: ResetPasswordComponent },
  { path: APP_ROUTES.PROFILE, component: ProfessionalProfilePageComponent },
  {
    path: APP_ROUTES.PROFILE_EXPERIENCE_STEP,
    loadComponent: () => import('./features/profile/pages/experience-step/experience-step.component').then(m => m.ExperienceStepComponent)
  },
  // Rutas de Maestro (sin prefijo, mantiene compatibilidad)
  {
    path: APP_ROUTES.INICIO,
    canActivate: [maestroGuard],
    loadComponent: () =>
      import('./features/home/home.page').then(m => m.HomePageComponent)
  },
  {
    path: APP_ROUTES.UNICON_CHAT,
    canActivate: [maestroGuard],
    loadComponent: () =>
      import('./features/page/unicon-chat/unicon-chat.component').then(m => m.UniconChatComponent)
  },

  // Rutas de Owner (con prefijo /owner)
  {
    path: 'owner',
    loadChildren: () => import('./features/owner/routes').then(m => m.OWNER_ROUTES)
  },
  {
    path: APP_ROUTES.QUOTE_STEP_1,
    loadComponent: () => import('./features/page/quote/quote-step-1.component').then(m => m.QuoteStep1Component)
  },
  {
    path: APP_ROUTES.QUOTE_PRE_LIST,
    canActivate: [QuoteFlowGuard],
    loadComponent: () => import('./features/page/quote-pre-list/quote-pre-list.component').then(m => m.QuotePreListComponent)
  },
  {
    path: APP_ROUTES.QUOTE_STEP_2,
    canActivate: [QuoteFlowGuard],
    loadComponent: () => import('./features/page/quote-step-2/quote-step-2.component').then(m => m.QuoteStep2Component)
  },
  {
    path: APP_ROUTES.QUOTE_ESTIMATION,
    canActivate: [QuoteFlowGuard],
    loadComponent: () => import('./features/page/quote-estimation/quote-estimation.component').then(m => m.QuoteEstimationComponent)
  },
  {
    path: APP_ROUTES.QUOTE_SUMMARY,
    loadComponent: () => import('./features/page/quote-summary/quote-summary.component').then(m => m.QuoteSummaryComponent)
  },
  {
    path: APP_ROUTES.QUOTE_FINAL,
    loadComponent: () => import('./features/page/quote-final/quote-final.component').then(m => m.QuoteFinalComponent)
  },
  {
    path: APP_ROUTES.QUOTE_SUCCESS,
    loadComponent: () => import('./features/page/quote-final/success-quote.component').then(m => m.SuccessQuoteComponent)
  },
  {
    path: APP_ROUTES.QUOTES_NEW,
    loadComponent: () => import('./features/page/quote/first/quote-first.component').then(m => m.QuoteFirstComponent)
  },
  {
    path: APP_ROUTES.QUOTES,
    pathMatch: 'full',
    loadComponent: () => import('./features/page/quote-list/quotes-list.component').then(m => m.QuotesListPage)
  },
  {
    path: APP_ROUTES.QUOTES_LIST,
    loadComponent: () => import('./features/page/quote-list/quotes-list.component').then(m => m.QuotesListPage)
  },
  {
    path: APP_ROUTES_WITH_PARAMS.QUOTES_DETAIL,
    loadComponent: () => import('./features/page/quote-detail/quote-detail.component').then(m => m.QuoteDetailComponent)
  },
  {
    path: APP_ROUTES_WITH_PARAMS.QUOTE_REVIEW,
    loadComponent: () => import('./features/page/quote-review/quote-review.component').then(m => m.QuoteReviewComponent)
  },
  {
    path: APP_ROUTES.QUOTE_SELECTED,
    loadComponent: () => import('./features/page/quote-selected/quote-selected.component').then(m => m.QuoteSelectedComponent)
  },
  {
    path: APP_ROUTES_WITH_PARAMS.QUOTE_PENDING,
    loadComponent: () => import('./features/page/quote-pending/quote-pending.component').then(m => m.QuotePendingComponent)
  },
  {
    path: APP_ROUTES.CONTRACT_NEW,
    loadComponent: () => import('./features/page/contract/contract-step-1.component').then(m => m.ContractStep1Component)
  },
  {
    path: APP_ROUTES.CONTRACT_STEP_2,
    loadComponent: () => import('./features/page/contract/contract-step-2.component').then(m => m.ContractStep2Component)
  },
  {
    path: APP_ROUTES.CONTRACT_STEP_3,
    loadComponent: () => import('./features/page/contract/contract-step-3.component').then(m => m.ContractStep3Component)
  },
  {
    path: APP_ROUTES.CONTRACT_SUMMARY,
    loadComponent: () => import('./features/page/contract/contract-summary.component').then(m => m.ContractSummaryComponent)
  },
  {
    path: APP_ROUTES_WITH_PARAMS.CONTRACTS_DETAIL,
    loadComponent: () => import('./features/page/contract/contract-detail.component').then(m => m.ContractDetailComponent)
  },
  {
    path: APP_ROUTES_WITH_PARAMS.PRELISTS_PDF,
    loadComponent: () => import('./features/page/pre-list-pdf/pre-list-pdf.component').then(m => m.PreListPdfComponent)
  },
  // Calculator component
  {
    path: NAVIGATION_ROUTES.CALCULATOR.replace(/^\//, ''),
    loadComponent: () => import('./features/landing/components/calculator/calculator.component').then(m => m.CalculatorComponent),
    canActivate: [CalculatorFlowGuard]
  },
  // Calculator confirm-materials
  {
    path: APP_ROUTES.CALCULATOR_CONFIRM_MATERIALS,
    loadComponent: () => import('./features/landing/components/calculator/flows/confirm-materials/confirm-materials.component').then(m => m.ConfirmMaterialsComponent),
    canActivate: [CalculatorFlowGuard]
  },
  // Calculator prelist-calculator
  {
    path: APP_ROUTES.CALCULATOR_PRELIST,
    loadComponent: () => import('./features/landing/components/calculator/flows/prelist-calculator/prelist-calculator.component').then(m => m.PrelistCalculatorComponent),
    canActivate: [CalculatorFlowGuard]
  },
  { path: '**', redirectTo: APP_ROUTES.ROOT }
];
